import { useState, useContext, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";

import { AppContexts } from "../../../providers";
import { useDebounce } from "../../../hook";

import { DATE_PRESETS, getPresetRange } from "./datePresets";

const FilterPanel = ({
  onSubmit = () => {},
  onFilterChange = () => {},
  searchTermPlaceholder,
  children,
}) => {
  const [filter, setFilter] = useState("");
  const [preset, setPreset] = useState("last-7");
  const [hasPendingDateChanges, setHasPendingDateChanges] = useState(false);
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const { register, handleSubmit, setValue } = useForm();
  const debouncedFilter = useDebounce(filter, 300);
  const skipPresetSync = useRef(null);
  const startField = register("start");
  const endField = register("end");

  const handleRegister = useCallback((data) => {
    skipPresetSync.current = [data.start, data.end];
    setHasPendingDateChanges(false);
    ReportsContext.updateRange(data.start, data.end);
    onSubmit(data.start, data.end);
  }, []);

  const handleFilterChange = useCallback((e) => setFilter(e.target.value), []);

  const handlePresetChange = useCallback(
    (e) => {
      const value = e.target.value;
      setPreset(value);
      setHasPendingDateChanges(value === "custom");
      const range = getPresetRange(value);
      if (range) {
        const start = range[0].format("YYYY-MM-DD");
        const end = range[1].format("YYYY-MM-DD");
        setValue("start", start);
        setValue("end", end);
        skipPresetSync.current = [start, end];
        ReportsContext.updateRange(start, end);
        onSubmit(start, end);
      }
    },
    [setValue, ReportsContext, onSubmit]
  );

  useEffect(
    () =>
      setValue("start", moment(ReportsContext.startDate).format("YYYY-MM-DD")),
    [ReportsContext.startDate]
  );
  useEffect(
    () => setValue("end", moment(ReportsContext.endDate).format("YYYY-MM-DD")),
    [ReportsContext.endDate]
  );
  useEffect(() => onFilterChange(debouncedFilter), [debouncedFilter]);

  // When context dates change externally, try to match a preset or fall back to "custom"
  useEffect(() => {
    const start = moment(ReportsContext.startDate);
    const end = moment(ReportsContext.endDate);
    const syncedRange = [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
    const rangeToSkip = skipPresetSync.current;
    skipPresetSync.current = null;
    if (
      rangeToSkip &&
      rangeToSkip[0] === syncedRange[0] &&
      rangeToSkip[1] === syncedRange[1]
    ) {
      return;
    }
    const matched = DATE_PRESETS.find((p) => {
      if (p.value === "custom") return false;
      const range = getPresetRange(p.value);
      return (
        range &&
        range[0].format("YYYY-MM-DD") === start.format("YYYY-MM-DD") &&
        range[1].format("YYYY-MM-DD") === end.format("YYYY-MM-DD")
      );
    });
    setPreset(matched ? matched.value : "custom");
  }, [ReportsContext.startDate, ReportsContext.endDate]);

  return (
    <form onSubmit={handleSubmit(handleRegister)}>
      <div className="uk-flex uk-flex-between uk-margin-small-bottom">
        <div className="uk-flex uk-flex-middle">
          <div className="uk-form-controls">
            <select
              className="uk-select"
              value={preset}
              onChange={handlePresetChange}
            >
              {DATE_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="uk-form-controls uk-margin-small-left">
            <div className="hms-date-picker">
              <input
                className="uk-input"
                type="date"
                max={moment().format("YYYY-MM-DD")}
                defaultValue={moment().subtract(7, "day").format("YYYY-MM-DD")}
                {...startField}
                onChange={(e) => {
                  startField.onChange(e);
                  setHasPendingDateChanges(true);
                }}
              />
            </div>
          </div>
          <div className="uk-form-controls uk-margin-small-left">
            <div className="hms-date-picker">
              <input
                className="uk-input"
                type="date"
                max={moment().format("YYYY-MM-DD")}
                defaultValue={moment().format("YYYY-MM-DD")}
                {...endField}
                onChange={(e) => {
                  endField.onChange(e);
                  setHasPendingDateChanges(true);
                }}
              />
            </div>
          </div>
          {children}
          {hasPendingDateChanges && (
            <div className="uk-margin-small-left">
              <button
                className="uk-button hms-btn hms-red-btn no-margin"
                type="submit"
              >
                Go
              </button>
            </div>
          )}
        </div>
        {searchTermPlaceholder ? (
          <div className="w-50 uk-margin-large-left">
            <div className="uk-search uk-search-default w-100">
              <span className="uk-search-icon-flip" uk-search-icon="true" />
              <input
                className="uk-search-input"
                type="search"
                value={filter}
                placeholder={searchTermPlaceholder}
                onChange={handleFilterChange}
              />
            </div>
          </div>
        ) : null}
      </div>
    </form>
  );
};

export default FilterPanel;
